import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Dropdown, DropdownTheme } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import { useMemo, useRef, useCallback } from "react";
import { useConfig } from "../../contexts/config-context.js";
import { useFocusFieldEvent } from "../../contexts/events-context.js";
import { useForcedState, ForcedState } from "../../contexts/forced-state-context.js";
import { useFocus } from "../../hooks/use-focus.js";
import { useFieldAttributes } from "../../services/field-attributes.js";
import { FieldDescriptionWUT } from "../field-description/field-description-wut.js";
import { FieldErrorWUT } from "../field-error/field-error-wut.js";
import { FieldLabelWUT } from "../field-label/field-label-wut.js";
import { Widget } from "../widget/widget.js";
import { classes } from "./dropdown-field-wut.st.css";
const getOptionId = (fieldId, index) => `${fieldId}-opt-${index}`;
const DropdownFieldWUT = ({
  id,
  isLoading,
  target,
  value,
  defaultValue,
  error,
  onChange,
  onFocus,
  onBlur,
  options = [],
  required = false,
  label,
  hideLabel = false,
  description,
  placeholder,
  disabled = false,
  readOnly,
  hasError = false,
  errorMessage,
  className,
  FieldLayout,
  requiredIndicatorType,
  requiredIndicatorPlacement,
  widgetHtml,
  widgetEnabled
}) => {
  const { isMobile } = useConfig();
  const valueByLabel = useMemo(
    () => options.reduce((result, option) => {
      result[option.label] = option.value;
      return result;
    }, {}),
    [options]
  );
  const selectableOptions = useMemo(
    () => options.map((option, index) => {
      return {
        id: getOptionId(id, index),
        value: option.label,
        isSelectable: true
      };
    }),
    [options, id]
  );
  const idByValue = useMemo(
    () => options.reduce((result, option, index) => {
      result[option.value] = getOptionId(id, index);
      return result;
    }, {}),
    [id, options]
  );
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const { labelId, errorId, descriptionId, ariaDescribedBy } = useFieldAttributes(id, error, description);
  const dropdownRef = useRef(null);
  useFocusFieldEvent(() => {
    dropdownRef.current?.focus({ preventScroll: true });
  }, id);
  const { handleBlur, containerRef } = useFocus({
    onBlur
  });
  const handleChange = useCallback(
    (option) => {
      const optionValue = option.value ? valueByLabel[option.value] : "";
      onChange(optionValue);
    },
    [valueByLabel, onChange]
  );
  const initialSelectedId = useMemo(
    () => value && idByValue[value] || defaultValue && idByValue[defaultValue] || String(Math.random() * Date.now()),
    [defaultValue, value, idByValue]
  );
  return /* @__PURE__ */ jsx(
    FieldLayout,
    {
      fieldId: id,
      renderLabel: () => /* @__PURE__ */ jsx(
        FieldLabelWUT,
        {
          labelId,
          required,
          label,
          hideLabel,
          requiredIndicatorType,
          requiredIndicatorPlacement
        }
      ),
      renderInput: () => widgetHtml && widgetEnabled ? /* @__PURE__ */ jsx(
        Widget,
        {
          fieldId: id,
          widgetHtml,
          onChange,
          value
        }
      ) : /* @__PURE__ */ jsx(
        "div",
        {
          onFocus,
          onBlur: handleBlur,
          ref: containerRef,
          "aria-label": required ? "required" : "",
          children: /* @__PURE__ */ jsx(
            Dropdown,
            {
              isLoading,
              "aria-labelledby": labelId,
              "data-hook": `dropdown-${id}`,
              appendTo: "parent",
              error: hasError,
              newErrorMessage: true,
              className: classNames(
                classes.root,
                classes.theme,
                {
                  [classes.error]: hasError,
                  [classes.hover]: forcedHover,
                  [classes.mobileDropdown]: isMobile
                },
                className
              ),
              placeholder,
              disabled,
              readOnly,
              name: label,
              options: selectableOptions,
              mobileNativeSelect: isMobile,
              initialSelectedId,
              onChange: handleChange,
              upgrade: true,
              "aria-label": label,
              theme: DropdownTheme.Box,
              "aria-describedby": ariaDescribedBy,
              "aria-invalid": hasError,
              ref: dropdownRef,
              flip: false
            },
            `${id}-${value}`
          )
        }
      ),
      renderDescription: () => /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          FieldErrorWUT,
          {
            target,
            hasError,
            errorMessage,
            id: errorId
          }
        ),
        /* @__PURE__ */ jsx(FieldDescriptionWUT, { description, id: descriptionId })
      ] })
    }
  );
};
export {
  DropdownFieldWUT
};
//# sourceMappingURL=dropdown-field-wut.js.map
